import { ReactElement, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { ROUTES } from '../../../helpers/constans';
import { resetPasswordSchema } from '../../../helpers/formik-shemas/auth.schema';
import { fetchResetPassword } from '../../../services/auth.service';
import { getAuthErrorMessage } from '../../../helpers/auth-error';
import { IInterceptorError } from '../../../types/@interceptor';
import AuthLayout from '../components/auth-layout';
import AuthTextField from '../components/auth-text-field';
import { styles } from '../components/auth-layout.styles';

interface IFormValues {
  email: string;
}

const ResetPassword = (): ReactElement => {
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const formik = useFormik<IFormValues>({
    initialValues: { email: '' },
    validationSchema: resetPasswordSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async ({ email }) => {
      setIsLoading(true);
      setFeedback(null);

      try {
        const response = await fetchResetPassword({ email });
        setFeedback({
          type: 'success',
          text: response.message || `If an account exists for ${email}, a reset link is on its way.`,
        });
      } catch (error) {
        setFeedback({
          type: 'error',
          text: getAuthErrorMessage(error as IInterceptorError),
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <AuthLayout
      title="Forgot password"
      subtitle="Enter the email on your account and we'll send a link to reset your password."
    >
      <Box
        component="form"
        sx={styles.form}
        onSubmit={formik.handleSubmit}
        noValidate
        aria-busy={isLoading}
      >
        {feedback ? (
          <Alert severity={feedback.type} sx={styles.alert} role="alert">
            {feedback.text}
          </Alert>
        ) : null}

        <AuthTextField
          name="email"
          label="Email"
          type="email"
          autoComplete="email"
          formik={formik}
          disabled={isLoading}
          icon={<MailOutlineIcon fontSize="small" sx={styles.fieldIcon} />}
        />

        <Button type="submit" sx={styles.submit} disabled={isLoading} fullWidth>
          {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Send reset link'}
        </Button>
      </Box>

      <Typography sx={styles.footer}>
        Remember your password?{' '}
        <Typography component={Link} to={ROUTES.login} sx={styles.textLink}>
          Back to sign in
        </Typography>
      </Typography>
    </AuthLayout>
  );
};

export default ResetPassword;
